import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  apiErrorText,
  useExportFleetXlsxMutation,
  useListFleetCategoriesQuery,
  useListFleetModelsQuery,
  type FleetModelFilter,
  type FleetStatus,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconPlus, IconUpload } from '@shared/ui/icons';
import { riseIn } from '@shared/theme/animations';
import { FleetImportDialog } from '@features/fleet-import';
import {
  fleetLeading,
  fleetType,
  groupModelsByCategory,
  isRecord,
  sortFleetModels,
  typo,
  type FleetSortKey,
  type SortDirection,
} from '@entities/fleet';
import { ModelDrawer, NEW_MODEL } from './ModelDrawer';
import { ModelTable } from './ModelTable';
import { RegistryFilters, RegistryResults, type RegistryView } from './RegistryToolbar';
import { Notice, TextButton } from './ui';

/** Параметр адреса с открытой карточкой: ссылку можно переслать, «Назад» закрывает. */
const MODEL_PARAM = 'model';

/** Метка в истории: карточку открыли из списка — закрытие возвращает «Назад». */
function isCardState(state: unknown): boolean {
  return isRecord(state) && state.fleetCard === true;
}

/** «Справочник техники 2026-10-20.xlsx» — как имя файла у backend, по местной дате. */
function exportFileName(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Справочник техники ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
}

/** Сохранить файл по object URL и сразу освободить его. */
function saveObjectUrl(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Root = styled.div`
  container: fleet / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

const ListBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Empty = styled.div`
  max-width: 640px;
  margin-top: ${({ theme }) => theme.spacing(8)};
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > div {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @container fleet (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

const NothingFound = styled.p`
  margin: ${({ theme }) => `${theme.spacing(8)} 0`};
  font-size: ${fleetType.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

function ListSkeleton() {
  return (
    <ListBox aria-busy="true" aria-label="Загрузка справочника">
      {Array.from({ length: 8 }, (_, i) => (
        <SkeletonLine key={i} $height={20} $width={i % 3 === 0 ? '40%' : '100%'} />
      ))}
    </ListBox>
  );
}

/**
 * Справочник техники: список моделей с фильтрами, карточка-паспорт в боковой
 * панели, правка и загрузка книги ЭММ (только с правом записи). Один и тот же
 * виджет годится и для агентов, которым справочник нужен только для чтения.
 */
export function FleetRegistry({ canWrite }: { canWrite: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const openId = new URLSearchParams(location.search).get(MODEL_PARAM);

  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<FleetStatus | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [staleOnly, setStaleOnly] = useState(false);
  const [view, setView] = useState<RegistryView>('grouped');
  const [sort, setSort] = useState<{ key: FleetSortKey; dir: SortDirection } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportXlsx, { isLoading: exporting }] = useExportFleetXlsxMutation();
  const [exportError, setExportError] = useState<string | null>(null);

  async function onExport() {
    setExportError(null);
    try {
      const { url } = await exportXlsx().unwrap();
      saveObjectUrl(url, exportFileName(new Date()));
    } catch (err) {
      setExportError(apiErrorText(err, 'Не удалось выгрузить справочник.'));
    }
  }

  // ПРЦ модели — объекта из шапки ОСНОВЫ (своя или общая); без объекта — общая.
  const { activeProjectId } = useActiveProjectContext();
  const filter = useMemo(() => {
    const f: FleetModelFilter = {};
    if (categoryId) f.category_id = categoryId;
    if (status) f.status = status;
    if (q) f.q = q;
    if (showArchived) f.include_archived = true;
    if (activeProjectId) f.project_id = activeProjectId;
    return f;
  }, [categoryId, status, q, showArchived, activeProjectId]);

  const { data, isLoading, isError, error, isFetching, refetch } = useListFleetModelsQuery(filter);
  const { data: categoryData } = useListFleetCategoriesQuery();
  const categories = categoryData?.items ?? [];

  const items = data?.items;
  const visible = useMemo(() => {
    const list = (items ?? []).filter((m) => !staleOnly || m.prc_stale);
    return view === 'flat' && sort ? sortFleetModels(list, sort.key, sort.dir) : list;
  }, [items, staleOnly, view, sort]);
  const groups = useMemo(
    () => (view === 'grouped' ? groupModelsByCategory(visible) : null),
    [view, visible],
  );
  const order = useMemo(
    () => (groups ? groups.flatMap((g) => g.models.map((m) => m.id)) : visible.map((m) => m.id)),
    [groups, visible],
  );

  const staleCount = (items ?? []).filter((m) => m.prc_stale).length;
  const units = visible.reduce((sum, m) => sum + m.units_count, 0);
  const filtered = Boolean(categoryId || status || q || showArchived || staleOnly);

  const withModel = useCallback(
    (id: string | null) => {
      const p = new URLSearchParams(location.search);
      if (id) p.set(MODEL_PARAM, id);
      else p.delete(MODEL_PARAM);
      const s = p.toString();
      return s ? `?${s}` : '';
    },
    [location.search],
  );

  const openModel = useCallback(
    (id: string) => navigate({ search: withModel(id) }, { state: { fleetCard: true } }),
    [navigate, withModel],
  );

  const switchModel = useCallback(
    (id: string) => navigate({ search: withModel(id) }, { replace: true, state: location.state }),
    [navigate, withModel, location.state],
  );

  const closeModel = useCallback(() => {
    if (isCardState(location.state)) navigate(-1);
    else navigate({ search: withModel(null) }, { replace: true });
  }, [navigate, withModel, location.state]);

  function resetFilters() {
    setSearch('');
    setCategoryId('');
    setStatus('');
    setShowArchived(false);
    setStaleOnly(false);
  }

  function onSort(key: FleetSortKey) {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'cost' || key === 'prc' || key === 'units' ? 'desc' : 'asc' },
    );
  }

  const registryEmpty = !isLoading && !isError && !filtered && (items?.length ?? 0) === 0;

  return (
    <Root>
      <RegistryFilters
        search={search}
        onSearch={setSearch}
        categories={categories}
        categoryId={categoryId}
        onCategory={setCategoryId}
        status={status}
        onStatus={setStatus}
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        filtered={filtered}
        onReset={resetFilters}
      />

      {isError ? (
        <ListBox>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось загрузить справочник техники.',
                'Нет доступа к справочнику техники.',
              )}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </ListBox>
      ) : isLoading ? (
        <ListSkeleton />
      ) : registryEmpty ? (
        <Empty>
          <h2>Справочник пока пуст</h2>
          <p>
            {typo(
              canWrite
                ? 'Загрузите книгу «Калькуляция ЭММ» — модели, категории и нормы заполнятся из листа «Справочник (паспорт техники)». Модель можно добавить и вручную.'
                : 'Справочник ведут экономисты: как только они загрузят книгу ЭММ, техника появится здесь.',
            )}
          </p>
          {canWrite && (
            <div>
              <Button type="button" $variant="primary" onClick={() => setImportOpen(true)}>
                <IconUpload size={16} />
                Загрузить из Excel
              </Button>
              <Button type="button" $variant="secondary" onClick={() => openModel(NEW_MODEL)}>
                <IconPlus size={16} />
                Добавить модель
              </Button>
            </div>
          )}
        </Empty>
      ) : (
        <>
          <RegistryResults
            models={visible.length}
            units={units}
            stale={staleCount}
            filtered={filtered}
            staleOnly={staleOnly}
            onStaleOnly={setStaleOnly}
            view={view}
            onView={setView}
            canWrite={canWrite}
            onImport={() => setImportOpen(true)}
            onCreate={() => openModel(NEW_MODEL)}
            onExport={() => void onExport()}
            exporting={exporting}
          />
          {exportError && (
            <ListBox>
              <Notice $tone="danger" role="alert">
                <span className="text">{exportError}</span>
                <TextButton type="button" onClick={() => setExportError(null)}>
                  Скрыть
                </TextButton>
              </Notice>
            </ListBox>
          )}
          <ListBox aria-busy={isFetching}>
            {visible.length === 0 ? (
              <NothingFound>
                Ничего не нашлось.{' '}
                <TextButton type="button" onClick={resetFilters}>
                  Сбросить фильтры
                </TextButton>
              </NothingFound>
            ) : (
              <ModelTable
                groups={groups}
                models={visible}
                sort={sort}
                onSort={onSort}
                onOpen={openModel}
              />
            )}
          </ListBox>
        </>
      )}

      {openId !== null && (openId !== NEW_MODEL || canWrite) && (
        <ModelDrawer
          modelId={openId}
          preview={items?.find((m) => m.id === openId)}
          canWrite={canWrite}
          siblings={order}
          onSwitch={switchModel}
          onClose={closeModel}
          onCreated={switchModel}
        />
      )}
      {canWrite && <FleetImportDialog open={importOpen} onClose={() => setImportOpen(false)} />}
    </Root>
  );
}
